import { useEffect, useRef } from 'react';
import { palette, transition } from '../../styles/tokens';

export interface DropdownMenuItem {
  icon?: React.ReactNode;
  label: string;
  subtitle?: string;
  onClick: () => void;
  destructive?: boolean;
}

export interface DropdownMenuSection {
  items: DropdownMenuItem[];
}

interface Props {
  sections: DropdownMenuSection[];
  isOpen: boolean;
  onClose: () => void;
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  width?: number;
}

export function DropdownMenu({ sections, isOpen, onClose, position = 'bottom-left', width = 220 }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', escHandler); };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const posStyle: React.CSSProperties = position === 'bottom-right'
    ? { top: '100%', right: 0, marginTop: 4 }
    : position === 'top-left'
    ? { bottom: '100%', left: 0, marginBottom: 4 }
    : position === 'top-right'
    ? { bottom: '100%', right: 0, marginBottom: 4 }
    : { top: '100%', left: 0, marginTop: 4 };

  return (
    <div ref={ref} style={{
      position: 'absolute', ...posStyle, zIndex: 50,
      width, background: palette.white, borderRadius: 12,
      border: `1px solid ${palette.g100}`,
      boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
      padding: 6, overflow: 'hidden',
      animation: 'dropdownIn 120ms ease-out',
    }}>
      <style>{`@keyframes dropdownIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }`}</style>
      {sections.map((section, si) => (
        <div key={si}>
          {si > 0 && <div style={{ height: 1, background: 'rgba(0,0,0,0.06)', margin: '4px 8px' }} />}
          {section.items.map((item, ii) => (
            <button key={ii} onClick={() => { item.onClick(); onClose(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '8px 10px', border: 'none', borderRadius: 8,
                background: 'transparent', cursor: 'pointer',
                fontFamily: 'inherit', textAlign: 'left',
                transition: `all ${transition.fast}`,
                color: item.destructive ? palette.red : palette.g700,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = item.destructive ? palette.redWash : 'rgba(15,108,189,0.06)';
                e.currentTarget.style.paddingLeft = '12px';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.paddingLeft = '10px';
              }}
            >
              {item.icon && <span style={{ flexShrink: 0, display: 'flex' }}>{item.icon}</span>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{item.label}</div>
                {item.subtitle && <div style={{ fontSize: 11, color: palette.g400, marginTop: 1 }}>{item.subtitle}</div>}
              </div>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
